import React, { useState, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import * as Utils from "../../Utils";
import "./style.css";

const useFakeMutation = () => {
  return useCallback(
    (user) =>
      new Promise((resolve, reject) => {
        setTimeout(() => {
          if (user.qty === "") {
            reject();
          } else {
            resolve(user);
          }
        }, 200);
      }),
    [],
  );
};

function computeMutation(newRow, oldRow) {
  if (newRow.length !== oldRow.length) {
    return `Length from '${oldRow.length}' to '${newRow.length}'`;
  }
  if (newRow.width !== oldRow.width) {
    return `Width from '${oldRow.width || ''}' to '${newRow.width || ''}'`;
  }
  if (newRow.height !== oldRow.height) {
    return `Depth from '${oldRow.height || ''}' to '${newRow.height || ''}'`;
  }
  if (newRow.qty !== oldRow.qty) {
    return `Quantity '${oldRow.qty || ''}' to '${newRow.qty || ''}'`;
  }
  return null;
}

export const ChecklistDataGrid = (props) => {
  const { rows } = props;
  const mutateRow = useFakeMutation();
  const noButtonRef = React.useRef(null);
  const [promiseArguments, setPromiseArguments] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState(null);

  const handleCloseSnackbar = () => setSnackbar(null);

  const updateNewRow = (oldRow, newRow) => {
    // Modify database values based on old configurations
    const updatedRows = rows.map((row) =>
      row.id === newRow.id ? { ...newRow } : row,
    );

    Utils.updateItemsInEditStore(updatedRows);
    props.sendDataToParent(updatedRows);
    if (props.onRowDataChange) {
      props.onRowDataChange(updatedRows); // Sync changes right back up into the parent's state engine cleanly
    }
  };

  const processRowUpdate = useCallback(
    (newRow, oldRow) =>
      new Promise((resolve, reject) => {
        const mutation = computeMutation(newRow, oldRow);
        if (mutation) {
          setPromiseArguments({ resolve, reject, newRow, oldRow });
        } else {
          resolve(oldRow);
        }
      }),
    [rows],
  );

  const handleNo = () => {
    const { oldRow, resolve } = promiseArguments;
    resolve(oldRow);
    setPromiseArguments(null);
  };

  const handleYes = async () => {
    const { newRow, oldRow, reject, resolve } = promiseArguments;
    try {
      const response = await mutateRow(newRow);
      updateNewRow(oldRow, newRow);
      setSnackbar({ children: "Successfully saved", severity: "success" });
      resolve(response);
    } catch (error) {
      setSnackbar({ children: "Field cannot be empty", severity: "error" });
      reject(oldRow);
    }
    setPromiseArguments(null);
  };

  const renderConfirmDialog = () => {
    if (!promiseArguments) {
      return null;
    }

    const { newRow, oldRow } = promiseArguments;
    const mutation = computeMutation(newRow, oldRow);

    return (
      <Dialog maxWidth="xs" open={!!promiseArguments}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent dividers>
          {`Pressing 'Yes' will change ${mutation}.`}
        </DialogContent>
        <DialogActions>
          <Button ref={noButtonRef} onClick={handleNo}>
            No
          </Button>
          <Button onClick={handleYes}>Yes</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div style={{ height: "100%", width: "100%" }}>
      {renderConfirmDialog()}
      <DataGrid
        // to handle data updates without breaking native component rendering cycles
        key={`datagrid-view-${rows.length}`}
        getRowId={(row) => row?.id || row?.niin}
        rows={rows} // Render direct data seamlessly
        columns={columns}
        processRowUpdate={processRowUpdate}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            fontWeight: "bold",
          },
        }}
      />
      {!!snackbar && (
        <Snackbar open onClose={handleCloseSnackbar} autoHideDuration={6000}>
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </div>
  );
};

const columns = [
  {
    field: "nsn",
    headerName: "NSN",
    flex: 1,
    minWidth: 107,
    editable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "niin",
    headerName: "NIIN",
    editable: false,
    flex: 1,
    minWidth: 107,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "name",
    headerName: "Part Number",
    flex: 1.2,
    minWidth: 125,
    editable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "nomenclature",
    headerName: "Nomenclature",
    flex: 1.52,
    minWidth: 150,
    editable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "length",
    headerName: "Unit Length",
    type: "number",
    editable: true,
    flex: 1,
    minWidth: 107,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "width",
    headerName: "Unit Width",
    type: "number",
    editable: true,
    flex: 1,
    minWidth: 107,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "height",
    headerName: "Unit Depth",
    type: "number",
    editable: true,
    flex: 1,
    minWidth: 107,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "authorizedqty",
    headerName: "Authorized Qty",
    type: "number",
    editable: false,
    width: 117,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "qty",
    headerName: "On Hand Qty",
    type: "number",
    editable: true,
    flex: 1,
    minWidth: 107,
    align: "center",
    headerAlign: "center",
  },
];

export default ChecklistDataGrid;
